import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function formatDate(dateStr?: string) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  const day = String(d.getDate()).padStart(2, "0");
  const months = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
  const month = months[d.getMonth()];
  const year = d.getFullYear();
  return `${day} ${month} ${year}`;
}
function stripHtml(html?: string) {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, "").trim();
}
function ChroniclesConsumer() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
        <div className="lg:col-span-7">
          <div className="aspect-[16/10] bg-muted/30 animate-pulse rounded-md" />
          <div className="mt-6 h-4 w-40 bg-muted/30 animate-pulse rounded" />
          <div className="mt-4 h-8 w-3/4 bg-muted/30 animate-pulse rounded" />
        </div>
        <div className="lg:col-span-5 flex flex-col gap-8">
          {[0, 1].map(i => <div key={i} data-index={i} className="flex flex-col gap-3">
              <div className="aspect-[4/3] bg-muted/30 animate-pulse rounded-md" />
              <div className="h-4 w-32 bg-muted/30 animate-pulse rounded" />
              <div className="h-6 w-2/3 bg-muted/30 animate-pulse rounded" />
            </div>)}
        </div>
      </div>;
  }
  if (error) {
    return <div className="text-sm text-muted-foreground">
        Gagal memuat artikel.{" "}
        <Button variant="link" onClick={refetch} className="px-1 h-auto">
          Coba lagi
        </Button>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <p className="text-muted-foreground font-mono text-sm">Belum ada artikel terbaru.</p>;
  }
  const featured = posts[0];
  const secondary = posts.slice(1, 3);
  return <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
      {}
      <article data-index={0} className="lg:col-span-7 group">
        <Link to={featured.link || "#"} className="block">
          <div className="overflow-hidden rounded-md bg-muted/20">
            <div className="aspect-[16/10] overflow-hidden">
              {featured.featuredImage ? <img src={featured.featuredImage} alt={featured.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" /> : <div className="h-full w-full bg-muted/30" />}
            </div>
          </div>

          <div className="mt-6 flex items-center gap-3 font-mono text-xs uppercase tracking-widest text-muted-foreground">
            <span>{formatDate(featured.date)}</span>
            {featured.categories && featured.categories.length > 0 && <>
                <span aria-hidden="true">·</span>
                <span className="text-primary">
                  {featured.categories[0].name}
                </span>
              </>}
          </div>

          <h3 className="mt-4 font-default font-bold text-3xl md:text-4xl lg:text-3xl xl:text-4xl leading-tight text-foreground">
            <span className="bg-[length:0%_1px] bg-no-repeat bg-[position:0_100%] group-hover:bg-[length:100%_1px] transition-[background-size] duration-500" style={{
            backgroundImage: `linear-gradient(currentColor, currentColor)`
          }}>
              {featured.title}
            </span>
          </h3>

          {featured.excerpt && <p className="mt-4 text-base leading-relaxed text-muted-foreground line-clamp-2 max-w-xl">
              {stripHtml(featured.excerpt)}
            </p>}

          {featured.author && <p className="mt-5 font-mono text-xs uppercase tracking-widest text-muted-foreground">
              Oleh <span className="text-foreground">{featured.author.name}</span>
            </p>}
        </Link>
      </article>

      {}
      <div className="lg:col-span-5 flex flex-col">
        {secondary.map((post, i) => <div key={post.id} data-index={i} className="group">
            {i > 0 && <div className="h-px w-full bg-border my-8" aria-hidden="true" />}
            <Link to={post.link || "#"} className="block">
              <div className="overflow-hidden rounded-md bg-muted/20">
                <div className="aspect-[4/3] overflow-hidden">
                  {post.featuredImage ? <img src={post.featuredImage} alt={post.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" /> : <div className="h-full w-full bg-muted/30" />}
                </div>
              </div>

              <div className="mt-4 flex items-center gap-3 font-mono text-xs uppercase tracking-widest text-muted-foreground">
                <span>{formatDate(post.date)}</span>
                {post.categories && post.categories.length > 0 && <>
                    <span aria-hidden="true">·</span>
                    <span className="text-primary">
                      {post.categories[0].name}
                    </span>
                  </>}
              </div>

              <h4 className="mt-3 font-default font-semibold text-xl md:text-2xl leading-snug text-foreground">
                <span className="bg-[length:0%_1px] bg-no-repeat bg-[position:0_100%] group-hover:bg-[length:100%_1px] transition-[background-size] duration-500" style={{
              backgroundImage: `linear-gradient(currentColor, currentColor)`
            }}>
                  {post.title}
                </span>
              </h4>
            </Link>
          </div>)}
      </div>
    </div>;
}
export default function FeaturedArticle() {
  const wp_query_params = {
    post_type: "post",
    per_page: 3,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="5166" id="featured-article" className="bg-background text-foreground py-20 md:py-28 lg:py-32 px-4 md:px-8 lg:px-12">
      <div className="max-w-7xl mx-auto">
        {}
        <div className="grid grid-cols-1 lg:grid-cols-10 gap-8 lg:gap-12 mb-16 md:mb-20">
          <div className="lg:col-span-6">
            <p className="font-mono text-xs uppercase tracking-widest text-primary mb-6">
              — BERITA TERKINI
            </p>
            <h2 className="font-default font-bold text-4xl md:text-5xl lg:text-5xl xl:text-6xl leading-[1.05] text-foreground">
              Warta Organisasi.
            </h2>
          </div>

          <div className="lg:col-span-4 flex flex-col lg:items-end lg:text-right justify-end">
            <p className="text-base leading-relaxed text-muted-foreground max-w-sm lg:ml-auto">
              Informasi terbaru, agenda kegiatan, dan pernyataan resmi dari organisasi kami.
            </p>
            <Link to="/NewsPage" className="mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-foreground hover:text-primary transition-colors">
              <span>Lihat semua berita</span>
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>

        {}
        <div className="h-px w-full bg-border mb-16 md:mb-20" aria-hidden="true" />

        {}
        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 3,
        order: "desc",
        orderby: "date"
      }}>
          <ChroniclesConsumer />
        </PostsProvider>
      </div>
    </section>;
}